import classNames from 'classnames';
import React from 'react';
import * as styles from './AttachmentItem.module.scss';
import IconButton from '../../components/IconButton';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import downloadIcon from '../../icons/non-prefilled/16x16/download.svg';
import downloadAllIcon from '../../icons/non-prefilled/16x16/downloadAll.svg';
import newTabIcon from '../../icons/non-prefilled/16x16/newTab.svg';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import attachmentIcon from '../../icons/prefilled/16x16/attachmentStroke.svg';
import Link from '../../utils/router/Link';
import getExtname from '../../utils/url/getExtname';
import useSourceWithAssetToken from '../AssetTokenContext';
import DetachIntoFileEmbedMenuItem from '../controls/DetachIntoFileEmbedMenuItem';
import type Attachment from '../formats/Attachment';
import type { AttachmentFile } from '../formats/Attachment';
import type AttachmentMention from '../formats/AttachmentMention';
import FileBlot from '../formats/FileBlot';
import { useQuill } from '../QuillContext';
import getDownloadLink from '../utils/getDownloadLink';
import getReadableSize from '../utils/getReadableSize';

const EXTENSIONS_FOR_NEW_TAB = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'txt',
  'md',
];

interface AttachmentProps {
  attachment: AttachmentFile;
  readOnly: boolean;
  isOverflowMenuOpen: boolean;
  onOverflowMenuOpenChange: (isOpen: boolean) => void;
  isAttachmentUploaded: (attachment: AttachmentFile) => boolean;
  onDeleteAttachment?: () => void;
  onDownloadAll: (() => void) | null;
  overflowMenuPortal?: HTMLElement;
  blot: Attachment | AttachmentMention;
}

const AttachmentItem = ({
  attachment,
  readOnly,
  onDeleteAttachment,
  isAttachmentUploaded,
  isOverflowMenuOpen,
  onOverflowMenuOpenChange,
  onDownloadAll,
  overflowMenuPortal,
  blot,
}: AttachmentProps) => {
  const { quill } = useQuill();
  const extname = getExtname(attachment.name);
  const isUploaded = isAttachmentUploaded(attachment);
  const downloadLink = useSourceWithAssetToken(
    isUploaded ? getDownloadLink(attachment) : null,
  );

  const attachmentSource = useSourceWithAssetToken(attachment.source);

  const item = (
    <div className={styles.attachment}>
      <div
        data-no-print
        className={classNames(styles.type, styles[extname.toLowerCase()])}
      >
        {extname.slice(0, 4)}
        <div className={styles.icon}>
          <SVGIcon>{attachmentIcon}</SVGIcon>
        </div>
      </div>
      <div className={styles.name}>{attachment.name}</div>
      <div className={styles.size}>{getReadableSize(attachment.size)}</div>
      <div className={styles.source}>
        <Link to={attachment.source}>{attachment.source}</Link>
      </div>
    </div>
  );

  const menuItems = [];

  if (isUploaded && EXTENSIONS_FOR_NEW_TAB.includes(extname.toLowerCase())) {
    menuItems.push(
      <MenuItem key="openNewTab" icon={newTabIcon} to={attachmentSource} newTab>
        Open in new tab
      </MenuItem>,
    );
  }

  if (
    !readOnly &&
    quill &&
    FileBlot.supportedExtensions.has(extname.toLowerCase())
  ) {
    menuItems.push(
      <DetachIntoFileEmbedMenuItem
        key="detachIntoFileEmbed"
        blot={blot}
        source={attachment.source}
        quill={quill}
      />,
    );
  }

  if (onDownloadAll) {
    menuItems.push(
      <MenuItem
        key="downloadAll"
        icon={downloadAllIcon}
        onClick={onDownloadAll}
      >
        Download all (zip)
      </MenuItem>,
    );
  }

  if (!readOnly && onDeleteAttachment) {
    if (menuItems.length) {
      menuItems.push(<MenuSeparator key="separator" />);
    }
    menuItems.push(
      <MenuItem
        icon={trashIcon}
        key="delete"
        danger
        onClick={onDeleteAttachment}
      >
        Remove this file
      </MenuItem>,
    );
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.menuOpen]: isOverflowMenuOpen,
        [styles.uploading]: !isUploaded,
      })}
    >
      {item}

      {(downloadLink || menuItems.length > 0) && (
        <div data-no-print className={styles.menu}>
          {downloadLink && (
            <Tooltip content="Download file">
              <IconButton
                to={downloadLink}
                target="_blank"
                disabled={!isUploaded}
                rel="noreferrer"
                aria-label="Download file"
                className={styles.menuButton}
              >
                <SVGIcon>{downloadIcon}</SVGIcon>
              </IconButton>
            </Tooltip>
          )}
          {menuItems.length > 0 && (
            <OverflowMenu
              open={isOverflowMenuOpen}
              onOpenChange={onOverflowMenuOpenChange}
              placement="bottom-start"
              portal={overflowMenuPortal}
              button={
                <IconButton
                  className={styles.menuButton}
                  aria-label="More options"
                  testId="attachmentItemOverflowMenuButton"
                >
                  <SVGIcon>{overflowMenuIcon}</SVGIcon>
                </IconButton>
              }
            >
              {menuItems}
            </OverflowMenu>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentItem;
